import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Modal } from '@babomrs/ui';
import { apiClient } from '@babomrs/api-client';
import {
  DEFAULT_IDLE_TIMEOUT_MINUTES,
  DEFAULT_WARNING_SECONDS,
  startIdleSignOut,
  type IdlePhase,
} from '../lib/idleSignOut';

export interface SessionPolicy {
  idleTimeoutMinutes: number;
  warningSeconds: number;
}

const FALLBACK: SessionPolicy = {
  idleTimeoutMinutes: DEFAULT_IDLE_TIMEOUT_MINUTES,
  warningSeconds: DEFAULT_WARNING_SECONDS,
};

/** "1:47", "0:05". */
function formatLeft(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Signs a client out after inactivity and warns them first (Story #22576).
 *
 * Mounted inside `ClientAppShell`, which renders only for a signed-in client, so the public
 * website is never timed out. The timeout is an Admin setting read from
 * `GET /api/v1/client/session-policy`. Until it arrives, or if the read fails, the default
 * applies — never "no timeout".
 */
export function IdleSignOutGuard({ onSignOut }: { onSignOut: () => void }) {
  const { data } = useQuery({
    queryKey: ['client', 'session-policy'],
    queryFn: () => apiClient.get<SessionPolicy>('/api/v1/client/session-policy'),
    staleTime: Infinity,
    retry: 1,
  });
  const policy = data ?? FALLBACK;

  const [phase, setPhase] = useState<IdlePhase>('active');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const controls = useRef<ReturnType<typeof startIdleSignOut> | null>(null);
  const stayRef = useRef<HTMLButtonElement>(null);
  // Held in a ref so a new callback identity does not restart the timer.
  const signOutRef = useRef(onSignOut);
  signOutRef.current = onSignOut;

  useEffect(() => {
    const c = startIdleSignOut({
      timeoutMinutes: policy.idleTimeoutMinutes,
      warningSeconds: policy.warningSeconds,
      onSignOut: () => signOutRef.current(),
      onChange: (p, left) => {
        setPhase(p);
        setSecondsLeft(left);
      },
    });
    controls.current = c;
    return () => c.stop();
  }, [policy.idleTimeoutMinutes, policy.warningSeconds]);

  const warningMinutes = Math.max(1, Math.round(policy.warningSeconds / 60));

  return (
    <Modal
      open={phase === 'warning'}
      onClose={() => controls.current?.staySignedIn()}
      title="Are you still there?"
      size="sm"
      role="alertdialog"
      initialFocusRef={stayRef}
      describedById="idle-signout-desc"
      footer={
        <>
          <Button variant="secondary" onClick={() => controls.current?.signOutNow()}>
            Sign Out Now
          </Button>
          <Button ref={stayRef} variant="primary" onClick={() => controls.current?.staySignedIn()}>
            Stay Signed In
          </Button>
        </>
      }
    >
      <p id="idle-signout-desc" className="text-sm text-[var(--text2)]">
        For your security, we&apos;ll sign you out in{' '}
        <span className="font-semibold text-[var(--text1)] tabular-nums" aria-hidden="true">
          {formatLeft(secondsLeft)}
        </span>
        <span className="sr-only">{`${warningMinutes} minute${warningMinutes === 1 ? '' : 's'}`}</span>{' '}
        because there has been no activity. Anything you haven&apos;t saved will be lost.
      </p>
    </Modal>
  );
}
